import { Button, Input, Modal, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import SettingNav from '../../ui/layout/SettingNav';
import WorkspaceHeader from '../../ui/layout/WorkspaceHeader';
import styles from './EnvironmentSettings.module.scss';
import CardTable from '../../components/Table/CardTable';
import NewButton from '../../components/NewButton/NewButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import {
  getAllClientEnvironments,
  createClientEnvironment,
  editClientEnvironment,
  removeClientEnvironment,
} from '../../infrastructure/apiClient';
import type { ClientEnvironmentDTO } from '../../infrastructure/models';
import { useClients } from '../../context/ClientContext';
import { useTableState } from '../../hooks/useTableState';
import { sortData, sortConfig, type SortConfig } from '../../utils/sortUtils';
import { getNextPageAfterDelete } from '../../utils/pagination';

interface EnvironmentSettingsProps {
  embedded?: boolean;
}

const SORT_CONFIGS: SortConfig<ClientEnvironmentDTO>[] = [
  sortConfig('name', 'string'),
  sortConfig('createdAt', 'date'),
  sortConfig('updatedAt', 'date'),
];

const EnvironmentSettings = ({ embedded = false }: EnvironmentSettingsProps) => {
  const [active, setActive] = useState<'user' | 'tenant' | 'environment'>('environment');
  const [environments, setEnvironments] = useState<ClientEnvironmentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  // Delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { activeClient } = useClients();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchEnvironments = useCallback(async (tenantId: string, offset: number, limit: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllClientEnvironments(tenantId, { offset, limit, signal });
      if (signal?.aborted) return;
      setEnvironments(response.data ?? []);
      updateTotal(typeof response.total === 'number' ? response.total : response.data?.length ?? 0);
    } catch (err) {
      if (signal?.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load client environments.';
      setError(errorMessage);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  const handlePageChange = useCallback(
    (offset: number, limit: number) => {
      if (!activeClient?.id) return;
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      fetchEnvironments(activeClient.id, offset, limit, controller.signal);
    },
    [activeClient?.id, fetchEnvironments],
  );

  const {
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateTotal,
    updateCurrentPage,
  } = useTableState<ClientEnvironmentDTO>({ onPageChange: handlePageChange });

  useEffect(() => {
    if (!activeClient?.id) {
      setEnvironments([]);
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchEnvironments(activeClient.id, 0, pagination.pageSize, controller.signal);
    return () => controller.abort();
  }, [activeClient?.id, fetchEnvironments, pagination.pageSize]);

  // ── Create ──
  const handleOpenCreateModal = () => setCreateModalOpen(true);

  const handleCloseCreateModal = () => {
    if (creating) return;
    setCreateModalOpen(false);
    setCreateName('');
    setCreateDescription('');
  };

  const handleCreate = async () => {
    const trimmedName = createName.trim();
    if (!trimmedName) {
      message.warning('Enter an environment name.');
      return;
    }
    try {
      setCreating(true);
      await createClientEnvironment(activeClient!.id, { name: trimmedName, description: createDescription.trim() });
      message.success('Environment created.');
      handleCloseCreateModal();
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchEnvironments(activeClient!.id, 0, pagination.pageSize, controller.signal);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Unable to create environment.');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit ──
  const handleOpenEditModal = (record: ClientEnvironmentDTO) => {
    setEditId(record.id);
    setEditName(record.name);
    setEditDescription(record.description ?? '');
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (editing) return;
    setEditModalOpen(false);
    setEditName('');
    setEditDescription('');
    setEditId(null);
  };

  const handleEdit = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName || !editId) {
      message.warning('Enter an environment name.');
      return;
    }
    try {
      setEditing(true);
      await editClientEnvironment(activeClient!.id, editId, { name: trimmedName, description: editDescription.trim() });
      message.success('Environment updated.');
      handleCloseEditModal();
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchEnvironments(activeClient!.id, 0, pagination.pageSize, controller.signal);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Unable to edit environment.');
    } finally {
      setEditing(false);
    }
  };

  // ── Delete ──
  const handleOpenDeleteConfirm = (record: ClientEnvironmentDTO) => {
    setDeleteId(record.id);
    setConfirmTitle('Delete environment');
    setConfirmMessage(`Are you sure you want to delete "${record.name}"?`);
    setConfirmOpen(true);
  };

  const handleConfirmCancel = useCallback(() => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setDeleteId(null);
    setConfirmTitle('');
    setConfirmMessage('');
  }, [confirmLoading]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteId) {
      handleConfirmCancel();
      return;
    }
    setConfirmLoading(true);
    try {
      await removeClientEnvironment(activeClient!.id, deleteId);
      message.success('Environment deleted.');
      const { nextPage, offset } = getNextPageAfterDelete({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        totalItems: pagination.total,
        deletedItems: 1,
      });
      updateCurrentPage(nextPage);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchEnvironments(activeClient!.id, offset, pagination.pageSize, controller.signal);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to delete environment.');
    } finally {
      setConfirmLoading(false);
      handleConfirmCancel();
    }
  }, [deleteId, handleConfirmCancel, pagination, updateCurrentPage, fetchEnvironments]);

  // ── Columns ──
  const columns: ColumnsType<ClientEnvironmentDTO> = [
    {
      title: 'Environment Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      sortOrder: sorter.columnKey === 'updatedAt' ? sorter.order : null,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <div className="flex gap-2">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit"
              onClick={() => handleOpenEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              aria-label="Delete"
              onClick={() => handleOpenDeleteConfirm(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const sortedData = useMemo(
    () => sortData(environments, sorter, SORT_CONFIGS),
    [environments, sorter],
  );

  const content = (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Client Environment Dashboard</h1>
        <div className={styles.actions}>
          <NewButton label="Add New Environment" onClick={handleOpenCreateModal} />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <CardTable<ClientEnvironmentDTO>
          className={styles.cardTable}
          title="Client Environments"
          columns={columns}
          dataSource={sortedData}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={tablePagination}
          onChange={handleTableChange}
          error={error ?? undefined}
        />
      </div>

      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        title="Create Client Environment"
        onCancel={handleCloseCreateModal}
        footer={null}
        destroyOnHidden
        width={520}
        classNames={{ body: 'min-h-[160px]' }}
      >
        <div className={styles.createForm}>
          <Input
            placeholder="Environment Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={creating}
            className={styles.createInput}
          />
          <Input.TextArea
            placeholder="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            disabled={creating}
            className={styles.createInput}
            rows={3}
          />
          <div className={styles.createActions}>
            <Button onClick={handleCreate} type="primary" loading={creating} disabled={creating}>
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        title="Edit Client Environment"
        onCancel={handleCloseEditModal}
        footer={null}
        destroyOnHidden
        width={520}
        classNames={{ body: 'min-h-[160px]' }}
      >
        <div className={styles.createForm}>
          <Input
            placeholder="Environment Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editing}
            className={styles.createInput}
          />
          <Input.TextArea
            placeholder="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            disabled={editing}
            className={styles.createInput}
            rows={3}
          />
          <div className={styles.createActions}>
            <Button onClick={handleCloseEditModal} disabled={editing}>
              Cancel
            </Button>
            <Button onClick={handleEdit} type="primary" loading={editing} disabled={editing}>
              Edit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleConfirmCancel}
      />
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className={styles.page}>
      <WorkspaceHeader />
      <div className={styles.body}>
        <aside className={styles.nav}>
          <SettingNav active={active} onChange={setActive} onHome={() => navigate('/workspace')} />
        </aside>
        <div className={styles.content}>{content}</div>
      </div>
    </div>
  );
};

export default EnvironmentSettings;
